import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { clientService } from '../../../services/clientService';
import { useAuth } from '../../../contexts/AuthContext';

const AllClientsModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadClients();
    }
  }, [isOpen, user?.id]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await clientService?.getAll(user?.id);
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients?.filter(client => {
    const searchLower = searchTerm?.toLowerCase();
    return (
      client?.name?.toLowerCase()?.includes(searchLower) ||
      client?.email?.toLowerCase()?.includes(searchLower) ||
      client?.phone?.toLowerCase()?.includes(searchLower)
    );
  });

  const handleClientClick = (client) => {
    setSelectedClient(client);
  };

  const getClientTypeLabel = (type) => {
    const types = {
      buyer: 'مشتري',
      renter: 'مستأجر',
      investor: 'مستثمر',
      seller: 'بائع'
    };
    return types?.[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-success bg-success/10',
      pending: 'text-warning bg-warning/10',
      inactive: 'text-muted-foreground bg-muted/10'
    };
    return colors?.[status] || 'text-muted-foreground bg-muted/10';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'نشط',
      pending: 'قيد الانتظار',
      inactive: 'غير نشط'
    };
    return labels?.[status] || status;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="جميع العملاء"
      size="large"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Icon
            name="Search"
            size={20}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="ابحث عن عميل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pr-10 pl-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground mt-2">جاري التحميل...</p>
          </div>
        ) : filteredClients?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">لا توجد عملاء</p>
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto space-y-2">
            {filteredClients?.map((client) => (
              <div
                key={client?.id}
                onClick={() => handleClientClick(client)}
                className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  selectedClient?.id === client?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="User" size={18} className="text-primary" />
                      <h4 className="font-semibold text-foreground">
                        {client?.name || 'عميل بدون اسم'}
                      </h4>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      {client?.email && (
                        <div className="flex items-center gap-2">
                          <Icon name="Mail" size={14} />
                          <span>{client?.email}</span>
                        </div>
                      )}
                      {client?.phone && (
                        <div className="flex items-center gap-2">
                          <Icon name="Phone" size={14} />
                          <span>{client?.phone}</span>
                        </div>
                      )}
                      {client?.clientType && (
                        <div className="flex items-center gap-2">
                          <Icon name="Tag" size={14} />
                          <span>{getClientTypeLabel(client?.clientType)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        getStatusColor(client?.status)
                      }`}
                    >
                      {getStatusLabel(client?.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            إجمالي العملاء: <span className="font-bold text-primary">{filteredClients?.length}</span>
          </p>
          <Button variant="outline" onClick={onClose}>
            إغلاق
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AllClientsModal;